import type { CSSProperties } from 'react';

import type { SubmitRevealTransitionPhase } from './useSubmitRevealTransition';

export interface SubmitRevealStageStylesOptions {
  prefersReducedMotion: boolean;
  /** Current phase from `useSubmitRevealTransition`. */
  transitionPhase: SubmitRevealTransitionPhase;
  /** Duration of the question-stage exit choreography. */
  submitTransitionMs: number;
  /** Duration of the results-stage entrance. */
  resultsEnterMs: number;
}

export interface SubmitRevealStageStyles {
  /** Wrapper style for the question stage (fades/blurs out while submitting). */
  questionStage: CSSProperties;
  /** Wrapper style for the results stage (slides/fades in once settled). */
  resultsStage: CSSProperties;
}

/**
 * Stage wrapper styles for the shared submit-to-results choreography
 * (companion to `useSubmitRevealTransition`). With reduced motion both
 * stages render without transition styling.
 */
export function createSubmitRevealStageStyles({
  prefersReducedMotion,
  transitionPhase,
  submitTransitionMs,
  resultsEnterMs,
}: SubmitRevealStageStylesOptions): SubmitRevealStageStyles {
  if (prefersReducedMotion) {
    return { questionStage: {}, resultsStage: {} };
  }

  const isSubmitting = transitionPhase === 'submitting';
  const isSettled = transitionPhase === 'results';

  return {
    questionStage: {
      opacity: isSubmitting ? 0 : 1,
      transform: isSubmitting ? 'translateY(-12px)' : 'translateY(0)',
      filter: isSubmitting ? 'blur(4px)' : 'blur(0px)',
      transition: `opacity ${submitTransitionMs}ms ease, transform ${submitTransitionMs}ms ease, filter ${submitTransitionMs}ms ease`,
    },
    resultsStage: {
      opacity: isSettled ? 1 : 0,
      transform: isSettled ? 'translateY(0)' : 'translateY(14px)',
      transition: `opacity ${resultsEnterMs}ms ease, transform ${resultsEnterMs}ms ease`,
    },
  };
}
